import React from 'react';

import { Box, Button, Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import * as amplitude from '@amplitude/analytics-browser';

import { ServiceThemeOptions } from '../../themes';
import { createHoverBackground } from '../../utils/ThemeUtils';

interface OnboardErrorAlertProps {
  errorText: string;
  helpText?: string;
  showEscapeHatch?: boolean;
  tsp: string;
  serviceName: string;
  onClose: () => void;
  onSkip: () => void;
  onHelpClick?: () => void;
}

/**
 * Enhanced error alert for onboarding with optional escape hatch
 */
const OnboardErrorAlert: React.FC<OnboardErrorAlertProps> = ({
  errorText,
  helpText,
  showEscapeHatch = false,
  tsp,
  serviceName,
  onClose,
  onSkip,
  onHelpClick,
}) => {
  const theme = useTheme() as ServiceThemeOptions;

  // Extract theme colors safely
  const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
  const textSecondary = (theme.palette?.text as any)?.secondary || '#757575';
  const dividerColor = (theme.palette?.divider as string) || '#E0E0E0';
  const fontFamily =
    (theme.typography as any)?.fontFamily || 'Inter, sans-serif';

  // Use theme-aware OnboardErrorAlert colors with fallbacks
  const alertBorderColor = theme.custom?.onboardErrorAlert?.border || '#fd9727';
  const alertBackgroundColor =
    theme.custom?.onboardErrorAlert?.background || '#fff3e5';
  const alertTextColor =
    theme.custom?.onboardErrorAlert?.textColor || '#000000';

  // Format error text to ensure it ends with punctuation
  const formatErrorText = (text: string): string => {
    const trimmedText = text.trim();
    const lastChar = trimmedText.slice(-1);
    const endsWithPunctuation = ['.', '!', '?'].includes(lastChar);

    return endsWithPunctuation ? trimmedText : `${trimmedText}.`;
  };

  const handleHelpClick = () => {
    amplitude.track('User Clicked Help from Error Alert', {
      tsp,
      serviceName,
      errorText,
      source: 'inline_error_alert',
    });

    // Don't track in Slack here - HelpModal will track when it opens
    if (onHelpClick) {
      onHelpClick();
    }
  };

  const handleSkipClick = () => {
    amplitude.track('User Clicked Skip from Error Alert', {
      tsp,
      serviceName,
      errorText,
      source: 'inline_error_alert',
    });

    // Don't track in Slack here - useSkipOnboarding will track when skip dialog opens
    // Use the onSkip prop to open SkipConfirmation modal
    onSkip();
  };

  return (
    <Box
      sx={{
        mb: 2,
        width: '100%',
        borderRadius: '8px',
        border: `1px solid ${alertBorderColor}`,
        backgroundColor: alertBackgroundColor,
        p: 2,
      }}
    >
      {/* Error title with inline icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: helpText ? 1 : showEscapeHatch ? 1.5 : 0,
        }}
      >
        {/* <ErrorOutlineIcon 
          sx={{ 
            color: errorColor,
            fontSize: '20px',
            mr: 1,
            flexShrink: 0,
          }}
        /> */}
        <Typography
          sx={{
            fontFamily: fontFamily,
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '20px',
            color: alertTextColor,
          }}
        >
          {formatErrorText(errorText)}
        </Typography>

        {/* Close button aligned to the right */}
        {/* <Button
          onClick={onClose}
          sx={{
            ml: 'auto',
            minWidth: 'auto',
            width: '24px',
            height: '24px',
            p: 0,
            color: errorColor,
            '&:hover': {
              backgroundColor: createHoverBackground(errorColor, 0.08),
            },
          }}
        >
          ×
        </Button> */}
      </Box>

      {/* Help text aligned with the title text */}
      {helpText && (
        <Box sx={{ ml: '0px' }}>
          {' '}
          {/* 20px icon + 8px margin */}
          <Typography
            variant='body2'
            sx={{
              mb: showEscapeHatch ? 1.5 : 0,
              fontFamily: fontFamily,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.15px',
              color: textSecondary,
              fontWeight: 400,
            }}
          >
            {helpText}
          </Typography>
        </Box>
      )}

      {/* Escape hatch section */}
      {showEscapeHatch && (
        <>
          <Divider
            sx={{
              my: 1.5,
              borderColor: dividerColor,
              opacity: 0.6,
            }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {/* Help Link */}
            {onHelpClick && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Typography
                  component='span'
                  sx={{
                    fontFamily: fontFamily,
                    fontSize: '12px',
                    lineHeight: '16px',
                    color: textSecondary,
                    fontWeight: 400,
                    opacity: 0.8,
                  }}
                >
                  Need help?{' '}
                </Typography>
                <Button
                  variant='text'
                  size='small'
                  onClick={handleHelpClick}
                  sx={{
                    color: primaryColor,
                    fontFamily: fontFamily,
                    textTransform: 'none',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '16px',
                    letterSpacing: '0.25px',
                    padding: '0px 4px',
                    minHeight: 'auto',
                    minWidth: 'auto',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: createHoverBackground(primaryColor, 0.0),
                    },
                    '&:active': {
                      backgroundColor: createHoverBackground(primaryColor, 0.0),
                    },
                    '&:focus': {
                      outline: 'none',
                      border: 'none',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Click here.
                </Button>
              </Box>
            )}

            {/* Skip Option */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography
                variant='caption'
                sx={{
                  fontFamily: fontFamily,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: textSecondary,
                  fontWeight: 400,
                  opacity: 0.8,
                }}
              >
                Can't resolve this issue?
              </Typography>
              <Button
                variant='text'
                size='small'
                onClick={handleSkipClick}
                sx={{
                  color: primaryColor,
                  fontFamily: fontFamily,
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '0.25px',
                  padding: '2px 6px',
                  minHeight: 'auto',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: createHoverBackground(primaryColor, 0.0),
                  },
                  '&:active': {
                    backgroundColor: createHoverBackground(primaryColor, 0.0),
                  },
                  '&:focus': {
                    outline: 'none',
                    border: 'none',
                    boxShadow: 'none',
                  },
                }}
              >
                Skip for now
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default OnboardErrorAlert;
